import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { API_URL, BACKEND_URL, PAYMENT_TIMEOUT_SECONDS } from '../config';
import { ErrorState, ErrorBanner } from '../components/ErrorState';
import { classifyError } from '../lib/apiError';
import { validateProviderAmount, providerAmountBounds } from '../lib/amountUtils';
import {
  Zap, Droplets, Tv, CreditCard, Check, ArrowLeft, MapPin, Loader2, Copy, Smartphone, Landmark, Wallet
} from "lucide-react";

const api = axios.create({ baseURL: API_URL });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

function validatedCustomerName(validation) {
  if (!validation) return null;
  const candidates = [
    validation.customer_name,
    validation.data?.customer_name,
    validation.data?.account_name,
    validation.data?.account_holder,
    validation.data?.account_holder_name,
    validation.data?.holder_name,
    validation.data?.name,
    validation.data?.full_name,
    validation.data?.subscriber_name,
    validation.data?.meter_owner,
    validation.data?.details,
  ];
  for (const value of candidates) {
    if (value != null && String(value).trim()) return String(value).trim();
  }
  return null;
}

function holderLabel(provider) {
  if (provider?.type === "electricity") return "Meter holder";
  if (provider?.type === "water") return "Account holder";
  if (provider?.type === "tax" || provider?.code === "MRA") return "Taxpayer";
  return "Account holder";
}

function isTaxProvider(provider) {
  return provider?.type === "tax" || provider?.code === "MRA";
}

function isPostpaidProvider(provider) {
  if (!provider) return false;
  if (provider.billing_mode === "postpaid") return true;
  const code = (provider.code || "").toUpperCase();
  if (code.endsWith("_POSTPAID")) return true;
  // Legacy water board codes are postpaid account bills.
  return ["LWB", "LLWB", "BWB", "NRWB", "NRW"].includes(code);
}

function isTokenlessProvider(provider) {
  return isTaxProvider(provider) || isPostpaidProvider(provider);
}

function providerDisplayName(provider) {
  if (!provider) return "";
  if (provider.name) return provider.name;
  return provider.code;
}

// MRA duty payments can be identified either by a per-bill Payment
// Registration Number (PRN) or by the taxpayer's standing TPIN.
function accountFieldLabel(provider, accountType) {
  if (isTaxProvider(provider)) {
    return accountType === "TPIN" ? "Taxpayer Identification Number (TPIN)" : "Duty Payment Reference (PRN)";
  }
  return provider?.account_label || "Meter Number";
}

function accountFieldPlaceholder(provider, accountType) {
  if (isTaxProvider(provider)) {
    return accountType === "TPIN" ? "Enter your TPIN" : "Enter your duty payment reference (PRN)";
  }
  return `Enter ${(provider?.account_label || "meter number").toLowerCase()}`;
}

function flowCopy(provider) {
  if (isTaxProvider(provider)) {
    return {
      progress: ["Provider", "Details", "Pay", "Done"],
      continueBtn: "Validate & continue",
      validatingBtn: "Validating reference...",
      confirmTitle: "Confirm payment",
      verifiedTitle: "Payment reference verified (PayChangu)",
      amountHint: "Enter the exact duty/tax amount from your MRA PRN or TPIN statement",
    };
  }
  if (isPostpaidProvider(provider)) {
    return {
      progress: ["Provider", "Details", "Confirm", "Done"],
      continueBtn: "Verify account",
      validatingBtn: "Validating Account...",
      confirmTitle: "Confirm Bill Payment",
      verifiedTitle: "Account verified (PayChangu)",
      amountHint: "Enter the amount due on your postpaid bill",
    };
  }
  return {
    progress: ["Provider", "Details", "Confirm", "Done"],
    continueBtn: "Verify meter",
    validatingBtn: "Validating Account...",
    confirmTitle: "Confirm Purchase",
    verifiedTitle: "Account verified (PayChangu)",
    amountHint: null,
  };
}

const CountdownTimer = ({ seconds, onExpire, label }) => {
  const [timeLeft, setTimeLeft] = useState(seconds);
  useEffect(() => {
    if (timeLeft <= 0) { onExpire?.(); return; }
    const timer = setInterval(() => setTimeLeft(prev => prev - 1), 1000);
    return () => clearInterval(timer);
  }, [timeLeft, onExpire]);
  const minutes = Math.floor(timeLeft / 60);
  const secs = timeLeft % 60;
  const isLow = timeLeft < 30;
  return (
    <div className={`text-center ${isLow ? "text-red-500" : "text-amber-500"}`}>
      <p className="text-sm">{label}</p>
      <p className={`text-2xl font-bold font-mono ${isLow ? "animate-pulse" : ""}`}>
        {String(minutes).padStart(2, "0")}:{String(secs).padStart(2, "0")}
      </p>
    </div>
  );
};

export default function BuyTokenPage() {
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [savedAccounts, setSavedAccounts] = useState([]);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [formData, setFormData] = useState({
    account_number: "",
    account_type: "PRN", // "PRN" or "TPIN" — only used for MRA tax payments
    amount: "",
    location: null,
    useGPS: false,
    package: null,
    customer_name: null,
  });
  const [transaction, setTransaction] = useState(null);
  // Mobile money direct charge (instant, in-app) vs hosted checkout redirect
  const [paymentMethod, setPaymentMethod] = useState("momo");
  const [momoNumber, setMomoNumber] = useState("");
  const [bankDetails, setBankDetails] = useState(null);
  const [walletBalance, setWalletBalance] = useState(null);
  const [paymentPin, setPaymentPin] = useState("");
  const [showPaymentPin, setShowPaymentPin] = useState(false);
  const [awaitingApproval, setAwaitingApproval] = useState(false);
  const [awaitingBankTransfer, setAwaitingBankTransfer] = useState(false);
  const pollCancelled = React.useRef(false);
  const appliedBuyQuery = useRef("");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => () => { pollCancelled.current = true; }, []);

  useEffect(() => {
    if (selectedProvider?.type === "tax" || selectedProvider?.code === "MRA") {
      setPaymentMethod("bank");
    }
  }, [selectedProvider]);

  useEffect(() => {
    if (step === 3) {
      api.get("/wallet/balance")
        .then((res) => setWalletBalance(res.data?.balance ?? 0))
        .catch(() => setWalletBalance(null));
    }
  }, [step]);

  const billerMapping = {
    ESCOM: "escom",
    ESCOM_PREPAID: "escom",
    ESCOM_POSTPAID: "escom",
    LWB: "llwb",
    LWB_PREPAID: "llwb",
    LWB_POSTPAID: "llwb",
    BWB: "bwb",
    BWB_PREPAID: "bwb",
    BWB_POSTPAID: "bwb",
    NRWB: "nrwb",
    NRWB_PREPAID: "nrwb",
    NRWB_POSTPAID: "nrwb",
    MRA: "mra",
    MASM: "masm",
  };

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    if (!providers.length) return;
    const key = searchParams.toString();
    if (appliedBuyQuery.current === key) return;
    appliedBuyQuery.current = key;
    const providerCode = searchParams.get("provider");
    if (!providerCode) return;
    const provider = providers.find((p) => p.code === providerCode);
    if (!provider) return;
    const meter = searchParams.get("meter") || searchParams.get("account") || "";
    const rawAmount = (searchParams.get("amount") || "").trim();
    const parsedAmount = Number(rawAmount);
    const amountValue = rawAmount && Number.isFinite(parsedAmount) && parsedAmount > 0 ? rawAmount : "";
    setSelectedProvider(provider);
    setStep(2);
    setValidationResult(null);
    setFormData((prev) => ({
      ...prev,
      account_number: meter,
      amount: amountValue,
      customer_name: null,
      package: null,
    }));
  }, [providers, searchParams]);

  const fetchData = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [providersRes, accountsRes] = await Promise.all([api.get("/providers"), api.get("/accounts")]);
      setProviders(providersRes.data);
      setSavedAccounts(accountsRes.data);
    } catch (error) {
      const classified = classifyError(error);
      setLoadError(classified);
      toast.error(classified.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGetLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (selectedProvider?.locations?.length > 0) {
            let nearest = selectedProvider.locations[0];
            let minDist = Infinity;
            selectedProvider.locations.forEach((loc) => {
              const dist = Math.sqrt(Math.pow(position.coords.latitude - loc.latitude, 2) + Math.pow(position.coords.longitude - loc.longitude, 2));
              if (dist < minDist) { minDist = dist; nearest = loc; }
            });
            setFormData((prev) => ({ ...prev, location: nearest, useGPS: true }));
            toast.success(`Location detected: ${nearest.name}`);
          }
        },
        () => toast.error("Could not get your location")
      );
    }
  };

  const handleSelectPackage = (pkg) => {
    setFormData((prev) => ({ ...prev, package: pkg, amount: pkg.price.toString() }));
  };

  const validateAccount = async () => {
    const billerId = billerMapping[selectedProvider.code];
    if (!billerId) return { success: true, skipped: true };
    try {
      const response = await api.post(
        "/paychangu/validate",
        {
          biller_id: billerId,
          account_number: formData.account_number,
          amount: parseFloat(formData.amount) || undefined,
          ...(isTaxProvider(selectedProvider) ? { account_type: formData.account_type } : {}),
        },
        { timeout: 120000 }
      );
      return response.data;
    } catch (error) {
      return { success: false, message: classifyError(error).message || "Validation failed" };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    if (!formData.account_number || !formData.amount) { toast.error("Please fill all required fields"); return; }
    if (selectedProvider?.type === "tv" && !formData.package) { toast.error("Please select a package"); return; }

    if (selectedProvider?.type !== "tv") {
      const amountCheck = validateProviderAmount(formData.amount, selectedProvider);
      if (!amountCheck.valid) {
        toast.error(amountCheck.message);
        return;
      }
    }

    const billerId = billerMapping[selectedProvider.code];
    const taxFlow = isTaxProvider(selectedProvider);
    if (billerId && selectedProvider.type !== "tv") {
      if (taxFlow && (!formData.amount || parseFloat(formData.amount) <= 0)) {
        toast.error("Amount is required to validate MRA duty payments");
        return;
      }
      const validationKey = `${formData.account_number}|${formData.amount}|${formData.account_type || ""}`;
      if (validationResult?.valid && validationResult._key === validationKey) {
        setStep(3);
        return;
      }
      setValidating(true);
      const validation = await validateAccount();
      setValidating(false);
      if (!validation.skipped) {
        const stamped = { ...validation, _key: validationKey };
        setValidationResult(stamped);
        if (!validation.success && !validation.valid) {
          setSubmitError({ kind: 'validation', message: validation.message || 'We could not verify that account/meter number.' });
          return;
        }
        const holderName = validatedCustomerName(validation);
        if (holderName) {
          setFormData((prev) => ({ ...prev, customer_name: holderName }));
        }
        if (validation.validation_degraded) {
          toast.warning(validation.message || "PayChangu validation timed out — you can still continue.");
        } else if (validation.valid && holderName) {
          toast.success(taxFlow ? `Reference verified: ${holderName}` : `Meter verified: ${holderName}`);
        } else if (validation.valid) {
          toast.success(taxFlow ? "Payment reference verified with PayChangu" : "Account verified with PayChangu");
        }
        if (validation.valid && !holderName && !validation.validation_degraded) {
          toast.warning("Account verified but meter holder name was not returned — you can still continue.");
        }
      }
      return;
    }
    setStep(3);
  };

  const createTransaction = async () => {
    const holderName =
      formData.customer_name || validatedCustomerName(validationResult);
    const txRes = await api.post("/transactions", {
      provider_code: selectedProvider.code,
      account_number: formData.account_number,
      amount: parseFloat(formData.amount),
      location: formData.location,
      package_name: formData.package?.name || null,
      ...(holderName ? { customer_name: holderName } : {}),
      ...(isTaxProvider(selectedProvider) ? { account_type: formData.account_type } : {}),
    });
    return txRes.data;
  };

  const pollPaymentStatus = async (txId) => {
    // Poll until the customer approves on their phone and the token is issued.
    // Bounded by the 5-minute payment window (backend expires the transaction).
    const startedAt = Date.now();
    const POLL_MS = 3000;
    const MAX_MS = 5 * 60 * 1000;
    while (!pollCancelled.current && Date.now() - startedAt < MAX_MS) {
      await new Promise((r) => setTimeout(r, POLL_MS));
      try {
        const res = await api.get(`/payments/status/${txId}`);
        const { status, token } = res.data;
        // Tokenless billers (MRA / postpaid utilities) confirm without a prepaid token.
        if (status === "success" && (token || isTokenlessProvider(selectedProvider))) {
          setTransaction(res.data);
          setAwaitingApproval(false);
          setAwaitingBankTransfer(false);
          setStep(4);
          toast.success("Payment confirmed — receipt ready!");
          return;
        }
        if (["failed", "expired"].includes(status)) {
          setAwaitingApproval(false);
          setAwaitingBankTransfer(false);
          setProcessing(false);
          toast.error(status === "expired" ? "Payment session expired." : "Payment was not completed.");
          return;
        }
        if (status === "fulfillment_failed") {
          setAwaitingApproval(false);
          setAwaitingBankTransfer(false);
          setProcessing(false);
          toast.error(res.data.fulfillment_error || "Payment received but delivery failed — check History to retry.");
          return;
        }
        if (res.data.bank_account && !bankDetails) {
          setBankDetails(res.data.bank_account);
        }
      } catch {
        // transient poll error — keep trying within the window
      }
    }
    if (!pollCancelled.current) {
      setAwaitingApproval(false);
      setAwaitingBankTransfer(false);
      setProcessing(false);
      toast.error("Payment not confirmed yet. If you paid, check History — your receipt may still arrive.");
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const handleConfirmPayment = async () => {
    if (paymentMethod === "wallet") {
      const amount = parseFloat(formData.amount);
      const total = amount * 1.04;
      if (walletBalance != null && walletBalance < total) {
        toast.error(`Insufficient wallet balance. Need MWK ${total.toLocaleString()}, have MWK ${walletBalance.toLocaleString()}`);
        return;
      }
      if (!paymentPin || paymentPin.length < 4) {
        setShowPaymentPin(true);
        return;
      }
    }
    if (paymentMethod === "momo") {
      const digits = momoNumber.replace(/\D/g, "");
      if (!(digits.length === 10 && digits.startsWith("0")) && !(digits.length === 12 && digits.startsWith("265"))) {
        toast.error("Enter your mobile money number, e.g. 0991234567");
        return;
      }
    }
    setProcessing(true);
    setSubmitError(null);
    try {
      if (paymentMethod === "wallet") {
        const payRes = await api.post("/wallet/pay", {
          provider_code: selectedProvider.code,
          account_number: formData.account_number,
          amount: parseFloat(formData.amount),
          payment_pin: paymentPin,
          customer_name: formData.customer_name || validatedCustomerName(validationResult),
          ...(isTaxProvider(selectedProvider) ? { account_type: formData.account_type } : {}),
        }, { timeout: 120000 });
        // Tokenless billers (MRA / postpaid utilities) confirm without a prepaid token.
        if (payRes.data.success && (payRes.data.token || isTokenlessProvider(selectedProvider))) {
          setTransaction({
            ...payRes.data,
            customer_name: formData.customer_name || validatedCustomerName(validationResult),
          });
          setStep(4);
          toast.success(payRes.data.token ? "Payment successful — token ready!" : "Payment successful — receipt confirmed!");
        }
        return;
      }
      const tx = await createTransaction();
      if (paymentMethod === "momo") {
        const chargeRes = await api.post("/payments/direct-charge", {
          transaction_id: tx.id,
          mobile: momoNumber,
        });
        if (chargeRes.data.success) {
          setAwaitingApproval(true);
          toast.success(`Approve the ${chargeRes.data.operator || "mobile money"} prompt on your phone`);
          pollPaymentStatus(tx.id);
        }
      } else if (paymentMethod === "bank") {
        const bankRes = await api.post("/payments/bank-transfer-charge", {
          transaction_id: tx.id,
        });
        if (bankRes.data.success) {
          setBankDetails(bankRes.data.bank_account);
          setAwaitingBankTransfer(true);
          toast.success("Transfer the exact amount to the bank account shown");
          pollPaymentStatus(tx.id);
        }
      } else {
        const payRes = await api.post(`/payments/initiate?transaction_id=${tx.id}`);
        if (payRes.data.payment_url) {
          if (tx.reference) {
            sessionStorage.setItem("pending_payment_ref", tx.reference);
          }
          toast.success("Redirecting to payment...");
          window.location.href = payRes.data.payment_url;
        } else if (payRes.data.success && payRes.data.transaction) {
          setTransaction(payRes.data.transaction);
          setStep(4);
          toast.success("Payment successful!");
        }
      }
    } catch (error) {
      const classified = classifyError(error);
      setSubmitError({ ...classified, message: classified.message || 'Failed to create transaction' });
      setProcessing(false);
      setAwaitingApproval(false);
      setAwaitingBankTransfer(false);
    }
  };

  const getProviderIcon = (provider) => {
    if (provider.type === "electricity") return <Zap className="w-6 h-6 text-yellow-500" />;
    if (provider.type === "water") return <Droplets className="w-6 h-6 text-cyan-500" />;
    if (provider.type === "tv") return <Tv className="w-6 h-6 text-blue-500" />;
    if (provider.type === "tax" || provider.code === "MRA") return <Landmark className="w-6 h-6 text-primary" />;
    return <CreditCard className="w-6 h-6 text-gray-500" />;
  };

  const getProviderColor = (provider) => {
    if (provider.type === "electricity") return "bg-yellow-500/20";
    if (provider.type === "water") return "bg-cyan-500/20";
    if (provider.type === "tv") return "bg-blue-500/20";
    if (provider.type === "tax" || provider.code === "MRA") return "bg-primary/20";
    return "bg-gray-500/20";
  };

  if (loading) return <div className="space-y-6"><div className="h-8 w-48 loading-shimmer rounded" /></div>;

  if (loadError) return <ErrorState error={loadError} onRetry={fetchData} />;

  const copy = flowCopy(selectedProvider);

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl mx-auto" data-testid="buy-token-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Pay a household bill</h1>
        <p className="text-muted-foreground text-sm">Pick a service, confirm your meter, and you're done in about 30 seconds.</p>
      </div>
      {/* Progress */}
      <div className="flex items-center gap-2 mb-8">
        {copy.progress.map((label, idx) => (
          <React.Fragment key={idx}>
            <div className={`flex items-center gap-2 ${step > idx ? "text-primary" : "text-muted-foreground"}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step > idx ? "bg-primary text-primary-foreground" : step === idx + 1 ? "bg-primary/20 text-primary" : "bg-muted"}`}>
                {step > idx + 1 ? <Check className="w-4 h-4" /> : idx + 1}
              </div>
              <span className="hidden sm:inline text-sm">{label}</span>
            </div>
            {idx < 3 && <div className={`flex-1 h-0.5 ${step > idx + 1 ? "bg-primary" : "bg-muted"}`} />}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Select Provider */}
      {step === 1 && (
        <div className="space-y-6">
          <h2 className="text-xl font-semibold">Select Service Provider</h2>
          {["electricity", "water", "tv", "tax"].map(type => {
            const typeProviders = providers.filter(p => p.type === type);
            if (typeProviders.length === 0) return null;
            return (
              <div key={type}>
                <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                  {type === "electricity" && <><Zap className="w-4 h-4 text-yellow-500" /> Electricity</>}
                  {type === "water" && <><Droplets className="w-4 h-4 text-cyan-500" /> Water</>}
                  {type === "tv" && <><Tv className="w-4 h-4 text-blue-500" /> TV Subscriptions</>}
                  {type === "tax" && <><Landmark className="w-4 h-4 text-primary" /> Tax & Government</>}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {typeProviders.map((provider) => (
                    <button key={provider.code} onClick={() => { setSelectedProvider(provider); setFormData(prev => ({ ...prev, package: null, amount: "", account_number: "", account_type: "PRN" })); setStep(2); }}
                      className="p-6 bg-card border border-border/50 rounded-xl text-left hover:border-primary/50 transition-all" data-testid={`provider-${provider.code}`}>
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${getProviderColor(provider)}`}>
                        {getProviderIcon(provider)}
                      </div>
                      <h3 className="font-semibold mb-1">{providerDisplayName(provider)}</h3>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {provider.billing_mode === "postpaid" || isPostpaidProvider(provider)
                          ? "Pay your monthly account bill"
                          : provider.billing_mode === "prepaid" || provider.type === "electricity" || provider.type === "water"
                            ? "Buy prepaid units / token"
                            : provider.name}
                      </p>
                      {provider.packages && <p className="text-xs text-blue-400 mt-2">{provider.packages.length} packages</p>}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Step 2: Enter Details */}
      {step === 2 && selectedProvider && (
        <div className="space-y-6">
          <button onClick={() => { setStep(1); setSelectedProvider(null); setFormData({ account_number: "", account_type: "PRN", amount: "", location: null, useGPS: false, package: null, customer_name: null }); setValidationResult(null); }} className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${getProviderColor(selectedProvider)}`}>
              {getProviderIcon(selectedProvider)}
            </div>
            <div>
              <h2 className="text-xl font-semibold">{providerDisplayName(selectedProvider)}</h2>
              <p className="text-sm text-muted-foreground">
                {isPostpaidProvider(selectedProvider) ? "Postpaid bill payment" : selectedProvider.name}
              </p>
            </div>
          </div>
          {savedAccounts.filter((a) => a.provider_code === selectedProvider.code).length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-2">Saved Accounts</h3>
              <div className="flex flex-wrap gap-2">
                {savedAccounts.filter((a) => a.provider_code === selectedProvider.code).map((account) => (
                  <button key={account.id} onClick={() => setFormData((prev) => ({ ...prev, account_number: account.account_number, location: account.location }))}
                    className={`px-4 py-2 rounded-lg border text-sm transition-all ${formData.account_number === account.account_number ? "bg-primary/20 border-primary text-primary" : "bg-card border-border hover:border-primary/50"}`}>
                    {account.nickname || account.account_number}
                  </button>
                ))}
              </div>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            {isTaxProvider(selectedProvider) && (
              <div>
                <label className="block text-sm font-medium mb-2">Pay using</label>
                <div className="grid grid-cols-2 gap-3">
                  {["PRN", "TPIN"].map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => {
                        setFormData((prev) => ({ ...prev, account_type: type, account_number: "", customer_name: null }));
                        setValidationResult(null);
                      }}
                      className={`p-3 rounded-xl border text-left transition-all ${formData.account_type === type ? "bg-primary/10 border-primary text-primary" : "bg-card border-border hover:border-primary/50"}`}
                      data-testid={`account-type-${type.toLowerCase()}`}
                    >
                      <p className="font-medium">{type}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {type === "PRN" ? "Per-bill payment reference" : "Taxpayer ID number"}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-2">{accountFieldLabel(selectedProvider, formData.account_type)}</label>
              <input
                type="text"
                value={formData.account_number}
                onChange={(e) => {
                  setFormData((prev) => ({ ...prev, account_number: e.target.value, customer_name: null }));
                  setValidationResult(null);
                }}
                placeholder={accountFieldPlaceholder(selectedProvider, formData.account_type)}
                className="w-full px-4 py-3 bg-input/50 border border-border rounded-xl focus:border-primary focus:ring-1 focus:ring-primary transition-all" data-testid="meter-input" />
            </div>
            {selectedProvider.type === "tv" && selectedProvider.packages && (
              <div>
                <label className="block text-sm font-medium mb-2">Select Package</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {selectedProvider.packages.map((pkg) => (
                    <button key={pkg.code} type="button" onClick={() => handleSelectPackage(pkg)}
                      className={`p-4 rounded-xl border text-left transition-all ${formData.package?.code === pkg.code ? "bg-blue-500/20 border-blue-500 text-blue-400" : "bg-card border-border hover:border-blue-500/50"}`} data-testid={`package-${pkg.code}`}>
                      <p className="font-medium">{pkg.name}</p>
                      <p className="text-lg font-bold mt-1">MWK {pkg.price.toLocaleString()}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {selectedProvider.type !== "tv" && (
              <div>
                <label className="block text-sm font-medium mb-2">Amount (MWK)</label>
                <input type="number" value={formData.amount} onChange={(e) => {
                  setFormData((prev) => ({ ...prev, amount: e.target.value, customer_name: null }));
                  setValidationResult(null);
                }}
                  placeholder="Enter amount"
                  min={isTaxProvider(selectedProvider) ? undefined : providerAmountBounds(selectedProvider).min}
                  max={isTaxProvider(selectedProvider) ? undefined : providerAmountBounds(selectedProvider).max}
                  step={isTaxProvider(selectedProvider) ? "any" : "100"}
                  className="w-full px-4 py-3 bg-input/50 border border-border rounded-xl focus:border-primary focus:ring-1 focus:ring-primary transition-all" data-testid="amount-input" />
                {copy.amountHint && (
                  <p className="text-xs text-primary mt-2">{copy.amountHint}</p>
                )}
                {!isPostpaidProvider(selectedProvider) && !isTaxProvider(selectedProvider) && searchParams.get("amount") && formData.amount === (searchParams.get("amount") || "").trim() && (
                  <p className="text-xs text-muted-foreground mt-2" data-testid="suggested-amount-hint">
                    Suggested for about 7 days of recent usage. You can change it.
                  </p>
                )}
                <div className="flex flex-wrap gap-2 mt-2">
                  {(isTaxProvider(selectedProvider) ? [500, 1000, 5000, 10000, 25000] : [1000, 2000, 5000, 10000]).map((amt) => (
                    <button key={amt} type="button" onClick={() => setFormData((prev) => ({ ...prev, amount: amt.toString() }))} className="px-3 py-1 text-sm bg-muted rounded-lg hover:bg-muted/80 transition-all">
                      MWK {amt.toLocaleString()}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {selectedProvider.type !== "tv" && selectedProvider.locations?.length > 0 && (
              <div>
                <label className="block text-sm font-medium mb-2">Location</label>
                <div className="flex gap-2 mb-2">
                  <button type="button" onClick={handleGetLocation} className="flex items-center gap-2 px-4 py-2 bg-muted rounded-lg hover:bg-muted/80 transition-all">
                    <MapPin className="w-4 h-4" /> Detect Location
                  </button>
                </div>
                <select value={formData.location?.code || ""} onChange={(e) => { const loc = selectedProvider.locations.find((l) => l.code === e.target.value); setFormData((prev) => ({ ...prev, location: loc, useGPS: false })); }}
                  className="w-full px-4 py-3 bg-input/50 border border-border rounded-xl focus:border-primary focus:ring-1 focus:ring-primary transition-all" data-testid="location-select">
                  <option value="">Select location</option>
                  {selectedProvider.locations.map((loc) => <option key={loc.code} value={loc.code}>{loc.name}</option>)}
                </select>
                {formData.location && <p className="text-sm text-emerald-500 mt-2 flex items-center gap-1"><MapPin className="w-4 h-4" />{formData.useGPS ? "GPS: " : ""}{formData.location.name}</p>}
              </div>
            )}
            {submitError && <ErrorBanner error={submitError} />}
            {(() => {
              const validationKey = `${formData.account_number}|${formData.amount}|${formData.account_type || ""}`;
              const holderName = formData.customer_name || validatedCustomerName(validationResult);
              const isValidated = validationResult?.valid && validationResult._key === validationKey;
              return (
                <>
                  {isValidated && (
                    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 space-y-2">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-500/20 rounded-full flex items-center justify-center">
                          <Check className="w-5 h-5 text-emerald-500" />
                        </div>
                        <div>
                          <p className="font-medium text-emerald-400">{copy.verifiedTitle}</p>
                          {holderName ? (
                            <p className="text-lg font-semibold text-foreground mt-1">
                              {holderLabel(selectedProvider)}: {holderName}
                            </p>
                          ) : (
                            <p className="text-sm text-amber-700 mt-1">
                              Account verified — meter holder name will be confirmed at payment.
                            </p>
                          )}
                        </div>
                      </div>
                      {validationResult?.validation_degraded && (
                        <p className="text-xs text-amber-700">
                          Validation was degraded — name may be confirmed when payment is processed.
                        </p>
                      )}
                    </div>
                  )}
                  <button type="submit" disabled={validating} className="w-full py-4 bg-primary text-primary-foreground rounded-xl font-semibold hover:bg-primary/90 transition-all disabled:opacity-50 flex items-center justify-center gap-2" data-testid="continue-btn">
                    {validating ? (
                      <><Loader2 className="w-5 h-5 animate-spin" />{copy.validatingBtn}</>
                    ) : isValidated ? (
                      "Continue to payment"
                    ) : (
                      copy.continueBtn
                    )}
                  </button>
                </>
              );
            })()}
          </form>
        </div>
      )}

      {/* Step 3: Confirmation */}
      {step === 3 && (
        <div className="space-y-6">
          <button
            onClick={() => {
              setStep(2);
              setValidationResult(null);
              setFormData((prev) => ({ ...prev, customer_name: null }));
            }}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <h2 className="text-xl font-semibold">{copy.confirmTitle}</h2>
          <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
            <CountdownTimer seconds={PAYMENT_TIMEOUT_SECONDS} onExpire={() => { toast.error("Payment session expired. Please try again."); setStep(2); setValidationResult(null); }} label="Complete payment within" />
          </div>
          {(validationResult?.valid || formData.customer_name) && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-500/20 rounded-full flex items-center justify-center"><Check className="w-5 h-5 text-emerald-500" /></div>
              <div>
                <p className="font-medium text-emerald-400">{copy.verifiedTitle}</p>
                {(formData.customer_name || validatedCustomerName(validationResult)) && (
                  <p className="text-lg font-semibold text-foreground mt-1">
                    {formData.customer_name || validatedCustomerName(validationResult)}
                  </p>
                )}
                {validationResult?.validation_degraded && (
                  <p className="text-xs text-amber-700 mt-1">Validation was degraded — name may be confirmed at payment.</p>
                )}
              </div>
            </div>
          )}
          <div className="bg-card border border-border/50 rounded-2xl p-6 space-y-4">
            <div className="flex justify-between items-center pb-4 border-b border-border/50">
              <span className="text-muted-foreground">Provider</span>
              <span className="font-medium">{selectedProvider.code}</span>
            </div>
            <div className="flex justify-between items-center pb-4 border-b border-border/50">
              <span className="text-muted-foreground">{accountFieldLabel(selectedProvider, formData.account_type)}</span>
              <span className="font-mono">{formData.account_number}</span>
            </div>
            {selectedProvider.type !== "tv" && (
              <div className="flex justify-between items-center pb-4 border-b border-border/50">
                <span className="text-muted-foreground">{holderLabel(selectedProvider)}</span>
                <span className="font-medium text-right max-w-[60%]">
                  {formData.customer_name || validatedCustomerName(validationResult) || "—"}
                </span>
              </div>
            )}
            {formData.package && (
              <div className="flex justify-between items-center pb-4 border-b border-border/50">
                <span className="text-muted-foreground">Package</span>
                <span className="text-blue-400">{formData.package.name}</span>
              </div>
            )}
            {formData.location && (
              <div className="flex justify-between items-center pb-4 border-b border-border/50">
                <span className="text-muted-foreground">Location</span>
                <span>{formData.location.name}</span>
              </div>
            )}
            <div className="flex justify-between items-center pb-4 border-b border-border/50">
              <span className="text-muted-foreground">Amount</span>
              <span className="text-lg font-bold">MWK {parseFloat(formData.amount).toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center pb-4 border-b border-border/50">
              <span className="text-muted-foreground">Platform Fee (4%)</span>
              <span className="text-orange-400">MWK {(parseFloat(formData.amount) * 0.04).toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Total</span>
              <span className="text-2xl font-bold text-primary">MWK {(parseFloat(formData.amount) * 1.04).toLocaleString()}</span>
            </div>
          </div>
          {/* Payment method */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-muted-foreground">Payment Method</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setPaymentMethod("wallet")}
                disabled={awaitingApproval || awaitingBankTransfer}
                className={`p-4 rounded-xl border text-left transition-all ${paymentMethod === "wallet" ? "bg-amber-500/10 border-amber-500 text-amber-600 dark:text-amber-400" : "bg-card border-border hover:border-amber-500/50"}`}
                data-testid="method-wallet"
              >
                <div className="flex items-center gap-2 font-medium"><Wallet className="w-5 h-5" /> SmartPay Wallet</div>
                <p className="text-xs mt-1 text-muted-foreground">
                  {walletBalance != null
                    ? `Balance: MWK ${walletBalance.toLocaleString()} — instant token`
                    : "Pay from your SmartPay balance"}
                </p>
              </button>
              <button
                type="button"
                onClick={() => setPaymentMethod("momo")}
                disabled={awaitingApproval || awaitingBankTransfer}
                className={`p-4 rounded-xl border text-left transition-all ${paymentMethod === "momo" ? "bg-emerald-500/10 border-emerald-500 text-emerald-500" : "bg-card border-border hover:border-emerald-500/50"}`}
                data-testid="method-momo"
              >
                <div className="flex items-center gap-2 font-medium"><Smartphone className="w-5 h-5" /> Mobile Money</div>
                <p className="text-xs mt-1 text-muted-foreground">Airtel / TNM — approve on your phone, instant token.</p>
              </button>
              <button
                type="button"
                onClick={() => setPaymentMethod("bank")}
                disabled={awaitingApproval || awaitingBankTransfer}
                className={`p-4 rounded-xl border text-left transition-all ${paymentMethod === "bank" ? "bg-primary/10 border-primary text-primary" : "bg-card border-border hover:border-primary/50"}`}
                data-testid="method-bank"
              >
                <div className="flex items-center gap-2 font-medium"><Landmark className="w-5 h-5" /> Bank Transfer</div>
                <p className="text-xs mt-1 text-muted-foreground">Instant transfer to a generated account — recommended for MRA tax payments.</p>
              </button>
              <button
                type="button"
                onClick={() => setPaymentMethod("checkout")}
                disabled={awaitingApproval || awaitingBankTransfer}
                className={`p-4 rounded-xl border text-left transition-all ${paymentMethod === "checkout" ? "bg-primary/10 border-primary text-primary" : "bg-card border-border hover:border-primary/50"}`}
                data-testid="method-checkout"
              >
                <div className="flex items-center gap-2 font-medium"><CreditCard className="w-5 h-5" /> Card / Other</div>
                <p className="text-xs mt-1 text-muted-foreground">PayChangu secure checkout (card, bank, mobile money).</p>
              </button>
            </div>
            {(selectedProvider?.type === "tax" || selectedProvider?.code === "MRA") && paymentMethod !== "bank" && (
              <p className="text-xs text-primary">MRA tax payments work best via Bank Transfer.</p>
            )}
            {paymentMethod === "wallet" && !awaitingApproval && (
              <div className="space-y-2">
                <label className="block text-sm font-medium">Payment PIN</label>
                <input
                  type="password"
                  inputMode="numeric"
                  maxLength={6}
                  value={paymentPin}
                  onChange={(e) => setPaymentPin(e.target.value.replace(/\D/g, ""))}
                  placeholder="Enter your wallet payment PIN"
                  className="w-full px-4 py-3 bg-input/50 border border-border rounded-xl text-center tracking-widest"
                  data-testid="wallet-pin-input"
                />
                <p className="text-xs text-muted-foreground">
                  Set your payment PIN in <button type="button" onClick={() => navigate("/wallet")} className="text-primary hover:underline">Wallet</button> if you haven&apos;t yet.
                </p>
              </div>
            )}
            {paymentMethod === "momo" && !awaitingApproval && (
              <div>
                <label className="block text-sm font-medium mb-2">Mobile Money Number</label>
                <input
                  type="tel"
                  value={momoNumber}
                  onChange={(e) => setMomoNumber(e.target.value)}
                  placeholder="e.g. 0991234567 (Airtel) or 0881234567 (TNM)"
                  className="w-full px-4 py-3 bg-input/50 border border-border rounded-xl focus:border-primary focus:ring-1 focus:ring-primary transition-all"
                  data-testid="momo-number-input"
                />
              </div>
            )}
          </div>

          {awaitingBankTransfer && bankDetails ? (
            <div className="p-6 bg-primary/10 border border-primary/30 rounded-xl space-y-4" data-testid="awaiting-bank-transfer">
              <div className="text-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-3" />
                <p className="font-semibold text-primary">Complete the bank transfer</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Open your bank app and use <strong>Instant Transfer</strong> to send the exact amount below.
                </p>
              </div>
              <div className="bg-card border border-border/50 rounded-xl p-4 space-y-3 text-sm">
                <div className="flex justify-between items-center gap-2">
                  <span className="text-muted-foreground">Bank</span>
                  <span className="font-medium">{bankDetails.bank_name}</span>
                </div>
                <div className="flex justify-between items-center gap-2">
                  <span className="text-muted-foreground">Account name</span>
                  <span className="font-medium">{bankDetails.account_name}</span>
                </div>
                <div className="flex justify-between items-center gap-2">
                  <span className="text-muted-foreground">Account number</span>
                  <button type="button" onClick={() => copyToClipboard(bankDetails.account_number, "Account number")} className="font-mono font-bold flex items-center gap-1 hover:text-primary">
                    {bankDetails.account_number} <Copy className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex justify-between items-center gap-2 pt-2 border-t border-border/50">
                  <span className="text-muted-foreground">Amount</span>
                  <button type="button" onClick={() => copyToClipboard(String(bankDetails.amount), "Amount")} className="text-lg font-bold text-primary flex items-center gap-1">
                    MWK {Number(bankDetails.amount).toLocaleString()} <Copy className="w-4 h-4" />
                  </button>
                </div>
                {bankDetails.expires_at && (
                  <p className="text-xs text-amber-600">Account expires {new Date(bankDetails.expires_at).toLocaleString()}</p>
                )}
              </div>
              <p className="text-xs text-center text-muted-foreground">We&apos;ll confirm automatically once PayChangu receives your transfer.</p>
            </div>
          ) : awaitingApproval ? (
            <div className="p-6 bg-emerald-500/10 border border-emerald-500/30 rounded-xl text-center space-y-3" data-testid="awaiting-approval">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-500 mx-auto" />
              <p className="font-semibold text-emerald-500">Approve the payment on your phone</p>
              <p className="text-sm text-muted-foreground">
                We sent a payment request to <span className="font-mono">{momoNumber}</span>.
                Enter your mobile money PIN on the prompt — {isTokenlessProvider(selectedProvider) ? "your receipt reference" : "your token"} will appear here the moment payment is confirmed.
              </p>
            </div>
          ) : (
            <>
              {submitError && <div className="mb-4"><ErrorBanner error={submitError} /></div>}
              <button onClick={handleConfirmPayment} disabled={processing} className="w-full py-4 bg-primary text-primary-foreground rounded-xl font-semibold hover:bg-primary/90 transition-all disabled:opacity-50 flex items-center justify-center gap-2" data-testid="confirm-payment-btn">
                {processing ? <><Loader2 className="w-5 h-5 animate-spin" />Processing...</> : paymentMethod === "wallet" ? <><Wallet className="w-5 h-5" />Pay from Wallet</> : <><CreditCard className="w-5 h-5" />Pay Now</>}
              </button>
            </>
          )}
        </div>
      )}

      {/* Step 4: Success */}
      {step === 4 && transaction && (
        <div className="text-center space-y-6">
          <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto"><Check className="w-10 h-10 text-emerald-500" /></div>
          <div>
            <h2 className="text-2xl font-bold mb-2">Payment Successful!</h2>
            {transaction.customer_name && (
              <p className="text-muted-foreground">Account: {transaction.customer_name}</p>
            )}
            <p className="text-muted-foreground">
              {selectedProvider?.type === "tv"
                ? "Your subscription has been renewed"
                : isTaxProvider(selectedProvider)
                  ? "Your MRA payment has been processed"
                  : isPostpaidProvider(selectedProvider)
                    ? "Your bill payment has been processed"
                    : "Your token has been generated"}
            </p>
          </div>
          <div className="bg-gradient-to-br from-emerald-500/20 to-blue-500/20 border border-emerald-500/30 rounded-2xl p-6 space-y-4">
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-2">
                {isTokenlessProvider(selectedProvider)
                  ? "Receipt Reference"
                  : selectedProvider?.type === "tv"
                    ? "Confirmation Code"
                    : "Your Token"}
              </p>
              <p className="font-mono text-xl font-bold text-emerald-400 break-all" data-testid="token-value">{transaction.token || transaction.reference}</p>
            </div>
            {transaction.token_units && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{selectedProvider?.type === "tv" ? "Days" : "Units"}</span>
                <span className="font-medium">{transaction.token_units}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Reference</span>
              <span className="font-mono text-xs">{transaction.reference}</span>
            </div>
          </div>
          <button onClick={() => { navigator.clipboard.writeText(transaction.token || transaction.reference); toast.success(isTokenlessProvider(selectedProvider) ? "Reference copied!" : "Token copied!"); }} className="w-full py-3 bg-muted rounded-xl font-medium hover:bg-muted/80 transition-all flex items-center justify-center gap-2" data-testid="copy-token-btn">
            <Copy className="w-4 h-4" /> {isTokenlessProvider(selectedProvider) ? "Copy Reference" : "Copy Token"}
          </button>
          <button onClick={() => navigate("/")} className="text-primary hover:underline">Back to Dashboard</button>
        </div>
      )}

      {/* Payment PIN modal for wallet */}
      {showPaymentPin && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowPaymentPin(false)}>
          <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">Enter Payment PIN</h3>
            <input
              type="password"
              inputMode="numeric"
              maxLength={6}
              value={paymentPin}
              onChange={(e) => setPaymentPin(e.target.value.replace(/\D/g, ""))}
              placeholder="4–6 digit PIN"
              className="w-full px-4 py-3 bg-input/50 border border-border rounded-xl text-center tracking-widest mb-4"
              autoFocus
            />
            <button
              onClick={() => { setShowPaymentPin(false); handleConfirmPayment(); }}
              disabled={processing || paymentPin.length < 4}
              className="w-full py-3 bg-primary text-primary-foreground rounded-xl font-medium disabled:opacity-50"
            >
              Confirm Payment
            </button>
          </div>
        </div>
      )}
    </motion.div>
  );
}